import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import ReactApexChart from "react-apexcharts";
import { useAuth } from "../../../../services/useAuth";
import { useTitle } from "../../../../hooks/useTitle";
import { withAuth } from "../../../../components/HOC/withAuth";
import AppPage from "../../../../components/CustomUI/AppPage";
import CardContainer from "../../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../../components/BaseUI/Table";
import { ToastContainer, Toast } from "../../../../components/CustomUI/Toast";
import Button from "../../../../components/BaseUI/Button";
import Lozenge from "../../../../components/BaseUI/Lozenge";
import { FiDownload } from "react-icons/fi";
import { useReportFilters } from "../shared/useReportFilters";
import ReportViewOptionsPanel from "../shared/ReportViewOptionsPanel";
import { useColumnVisibility } from "../../../../hooks/useColumnVisibility";
import {
  KpiCard,
  SectionSpinner,
  SectionEmpty,
  fmtDT,
  toISO,
  exportXLSX,
} from "../shared/utils";
import {
  API_URL,
  DEVICE_STATUSES,
  STATUS_TR,
  STATUS_COLORS,
  STATUS_APPEARANCE,
} from "../shared/constants";
import "../Reports.scss";

const MODEL_TABLE_COLUMNS = [
  "Model",
  "Montaj",
  "Paketleme",
  "Stok",
  "Teslim",
  "Arızalı",
  "Teslim Verimi",
].map((name, id) => ({ name, id, sortable: false }));

const TRANSITION_TABLE_COLUMNS = [
  "Seri No",
  "Model",
  "Önceki Durum",
  "Yeni Durum",
  "Değiştiren",
  "Tarih / Saat",
  "Notlar",
].map((name, id) => ({ name, id, sortable: false }));

const DeviceStatusReport = () => {
  useTitle("Rovion MES - Ürün Analizi");
  const { getToken } = useAuth();
  const filters = useReportFilters();
  const {
    visibleColumns: modelVisibleColumns,
    toggleColumnVisibility: toggleModelColumn,
    resetToDefault: resetModelColumns,
  } = useColumnVisibility("device-status-model-list", MODEL_TABLE_COLUMNS);
  const {
    visibleColumns: transitionVisibleColumns,
    toggleColumnVisibility: toggleTransitionColumn,
    resetToDefault: resetTransitionColumns,
  } = useColumnVisibility("device-status-transition-list", TRANSITION_TABLE_COLUMNS);

  const [machines, setMachines] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [deviceModels, setDeviceModels] = useState([]);
  const [transData, setTransData] = useState(null);
  const [transLoading, setTransLoading] = useState(false);
  const [statusAnData, setStatusAnData] = useState(null);
  const [statusAnLoading, setStatusAnLoading] = useState(false);
  const [toasts, setToasts] = useState([]);

  const authH = useCallback(() => ({ headers: { Authorization: `Bearer ${getToken()}` } }), [getToken]);
  const addToast = useCallback((msg, type = "success") => setToasts((p) => [...p, { id: Date.now(), message: msg, type }]), []);
  const dismissToast = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);

  useEffect(() => {
    (async () => {
      try {
        const [mR, sR, dmR] = await Promise.all([
          axios.get(`${API_URL}/api/machines`, authH()),
          axios.get(`${API_URL}/api/shifts`, authH()),
          axios.get(`${API_URL}/api/device-models`, authH()),
        ]);
        setMachines(mR.data.machines || mR.data || []);
        setShifts(sR.data.shifts || []);
        setDeviceModels(dmR.data.deviceModels || dmR.data || []);
      } catch {}
    })();
  }, [authH]);

  const fetchTransitions = useCallback(async () => {
    setTransLoading(true);
    try {
      const p = new URLSearchParams();
      p.set("startDate", toISO(filters.startDate, false));
      p.set("endDate", toISO(filters.endDate, true));
      if (filters.transFromStatus) p.set("fromStatus", filters.transFromStatus);
      if (filters.transToStatus) p.set("toStatus", filters.transToStatus);
      if (filters.transModelId) p.set("modelId", filters.transModelId);
      const r = await axios.get(`${API_URL}/api/reports/device-transitions?${p}`, authH());
      setTransData(r.data.data);
    } catch {
      addToast("Ürün geçiş verisi yüklenemedi", "danger");
      setTransData(null);
    } finally {
      setTransLoading(false);
    }
  }, [filters.startDate, filters.endDate, filters.transFromStatus, filters.transToStatus, filters.transModelId, authH, addToast]);

  const fetchStatusAnalytics = useCallback(async () => {
    setStatusAnLoading(true);
    try {
      const p = new URLSearchParams();
      p.set("startDate", toISO(filters.startDate, false));
      p.set("endDate", toISO(filters.endDate, true));
      if (filters.transModelId) p.set("modelId", filters.transModelId);
      const r = await axios.get(`${API_URL}/api/reports/device-status-analytics?${p}`, authH());
      setStatusAnData(r.data.data);
    } catch {
      addToast("Ürün durum analitiği yüklenemedi", "danger");
      setStatusAnData(null);
    } finally {
      setStatusAnLoading(false);
    }
  }, [filters.startDate, filters.endDate, filters.transModelId, authH, addToast]);

  const fetchAll = useCallback(() => {
    fetchTransitions();
    fetchStatusAnalytics();
  }, [fetchTransitions, fetchStatusAnalytics]);

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const maxCellVal = useMemo(() => {
    if (!transData?.matrix) return 1;
    let max = 0;
    DEVICE_STATUSES.forEach((fr) =>
      DEVICE_STATUSES.forEach((to) => { if (fr !== to) max = Math.max(max, transData.matrix[fr]?.[to] || 0); }),
    );
    return max || 1;
  }, [transData]);

  const cellBg = (val) => !val ? "transparent" : `rgba(99, 102, 241, ${Math.min(0.85, (val / maxCellVal) * 0.85)})`;
  const cellColor = (val) => !val ? "#9ca3af" : val / maxCellVal > 0.4 ? "#fff" : "#1a2236";

  const analytics = statusAnData;

  // Derived KPI summary
  const kpiSummary = useMemo(() => {
    if (!analytics?.uniqueCounts) return null;
    const u = analytics.uniqueCounts;
    const total = DEVICE_STATUSES.reduce((s, st) => s + (u[st] || 0), 0);
    const deliveredYield = (u.delivered || 0) + (u.defective || 0) > 0
      ? Math.round(((u.delivered || 0) / ((u.delivered || 0) + (u.defective || 0))) * 1000) / 10
      : null;
    return { total, deliveredYield, ...u };
  }, [analytics]);

  const exportAnalytics = () => {
    const mb = analytics?.modelBreakdown || [];
    if (!mb.length) return;
    const rows = mb.map((m) => ({
      Model: m.modelName,
      Montaj: m.counts?.assembly || 0,
      Paketleme: m.counts?.packaging || 0,
      Stok: m.counts?.stock || 0,
      Teslim: m.counts?.delivered || 0,
      Arızalı: m.counts?.defective || 0,
    }));
    const totalsRow = rows.reduce(
      (acc, r) => ({ ...acc, Montaj: acc.Montaj + r.Montaj, Paketleme: acc.Paketleme + r.Paketleme, Stok: acc.Stok + r.Stok, Teslim: acc.Teslim + r.Teslim, Arızalı: acc.Arızalı + r.Arızalı }),
      { Model: "TOPLAM", Montaj: 0, Paketleme: 0, Stok: 0, Teslim: 0, Arızalı: 0 },
    );
    exportXLSX([...rows, totalsRow], `ürün-durum-analizi-${filters.startDate}-${filters.endDate}`, "Ürün Durum Analitiği");
  };

  const exportTrans = () => {
    if (!transData?.rows?.length) return;
    exportXLSX(
      transData.rows.map((r) => ({
        "Seri No": r.serialNumber,
        Model: r.modelName,
        "Önceki Durum": STATUS_TR[r.fromStatus] || r.fromStatus || "—",
        "Yeni Durum": STATUS_TR[r.toStatus] || r.toStatus,
        Değiştiren: r.changedByName,
        "Tarih / Saat": fmtDT(r.changedAt),
        Notlar: r.notes,
      })),
      `ürün-durum-gecisleri-${filters.startDate}-${filters.endDate}`,
      "Ürün Durum Geçişleri",
    );
  };

  const isLoading = statusAnLoading || transLoading;

  return (
    <AppPage
      title="Ürün Analizi"
      actions={
        <div className="d-flex gap-2 flex-wrap align-items-center">
          <ReportViewOptionsPanel
            filters={filters}
            machines={machines}
            shifts={shifts}
            deviceModels={deviceModels}
            onApply={fetchAll}
            showTransFilters={true}
            showMachines={false}
            columnSections={[
              {
                id: "model-columns",
                title: "Model Tablosu Sütunları",
                columns: MODEL_TABLE_COLUMNS,
                visibleColumns: modelVisibleColumns,
                onToggle: toggleModelColumn,
                onReset: resetModelColumns,
              },
              {
                id: "transition-columns",
                title: "Geçiş Tablosu Sütunları",
                columns: TRANSITION_TABLE_COLUMNS,
                visibleColumns: transitionVisibleColumns,
                onToggle: toggleTransitionColumn,
                onReset: resetTransitionColumns,
              },
            ]}
          />
          <Button appearance="primary-subtle" onClick={exportAnalytics} iconBefore={<FiDownload size={13} />} disabled={!analytics?.modelBreakdown?.length}>
            Dışa Aktar
          </Button>
        </div>
      }
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast key={t.id} title={t.message} appearance={t.type === "danger" ? "danger" : "success"} onClose={() => dismissToast(t.id)} />
        ))}
      </ToastContainer>

      <div className="d-flex flex-column gap-3">
        {isLoading && <SectionSpinner />}

        {!isLoading && !analytics && !transData && (
          <SectionEmpty msg="Filtreyi uygulayarak yükleyin." />
        )}

        {/* KPI summary row */}
        {!isLoading && kpiSummary && (
          <div className="rp-grid-5" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
            {DEVICE_STATUSES.map((s) => (
              <KpiCard
                key={s}
                label={STATUS_TR[s]}
                value={(analytics?.uniqueCounts?.[s] || 0).toLocaleString("tr-TR")}
                sub={<Lozenge appearance={STATUS_APPEARANCE[s]}>{STATUS_TR[s]}</Lozenge>}
              />
            ))}
          </div>
        )}

        {/* Yield KPI */}
        {!isLoading && kpiSummary?.deliveredYield != null && (
          <div className="rp-grid-4">
            <KpiCard
              label="Teslim Verimi"
              value={`${kpiSummary.deliveredYield}%`}
              sub={<Lozenge appearance={kpiSummary.deliveredYield >= 90 ? "success-subtle" : kpiSummary.deliveredYield >= 70 ? "warning-subtle" : "danger-subtle"} isBold>
                {kpiSummary.deliveredYield >= 90 ? "Yüksek" : kpiSummary.deliveredYield >= 70 ? "Orta" : "Düşük"}
              </Lozenge>}
            />
            <KpiCard label="Toplam Teslim" value={(analytics?.uniqueCounts?.delivered || 0).toLocaleString("tr-TR")} />
            <KpiCard label="Toplam Arızalı" value={(analytics?.uniqueCounts?.defective || 0).toLocaleString("tr-TR")} referenceColor="#e74c3c" />
            <KpiCard label="Toplam Geçiş" value={(transData?.totalTransitions || 0).toLocaleString("tr-TR")} />
          </div>
        )}

        {/* Monthly flow chart + Transition matrix */}
        {!isLoading && (
          (Array.isArray(analytics?.timelineMonthsUnique) && analytics.timelineMonthsUnique.length > 0) ||
          (Array.isArray(analytics?.timelineMonths) && analytics.timelineMonths.length > 0) ||
          transData?.matrix
        ) && (
          <div className="rp-grid-2">
            {Array.isArray(analytics?.timelineMonthsUnique) && analytics.timelineMonthsUnique.length > 0 ? (
              <CardContainer title="Aylık Üretim Akışı (Cihaz Başına)">
                <ReactApexChart
                  type="bar"
                  series={DEVICE_STATUSES.map((s) => ({
                    name: STATUS_TR[s] || s,
                    data: analytics.timelineMonthsUnique.map((x) => x.counts?.[s] || 0),
                  }))}
                  options={{
                    chart: { type: "bar", stacked: true, toolbar: { show: true, tools: { download: true } }, fontFamily: "inherit", foreColor: "#333" },
                    plotOptions: { bar: { horizontal: false, columnWidth: "70%", borderRadius: 3 } },
                    colors: DEVICE_STATUSES.map((s) => STATUS_COLORS[s]),
                    xaxis: { categories: analytics.timelineMonthsUnique.map((x) => x.month), labels: { rotate: -45, style: { fontSize: "11px", fontWeight: 500 } } },
                    yaxis: { labels: { formatter: (val) => Math.round(val) } },
                    legend: { position: "top", horizontalAlign: "right", fontSize: "12px" },
                    dataLabels: { enabled: false },
                    grid: { borderColor: "#f0f2f5" },
                    tooltip: { shared: true, intersect: false },
                  }}
                  height={300}
                />
              </CardContainer>
            ) : Array.isArray(analytics?.timelineMonths) && analytics.timelineMonths.length > 0 ? (
              <CardContainer title="Aylık Durum Dağılımı">
                <ReactApexChart
                  type="bar"
                  series={DEVICE_STATUSES.map((s) => ({
                    name: STATUS_TR[s] || s,
                    data: analytics.timelineMonths.map((x) => x.counts?.[s] || 0),
                  }))}
                  options={{
                    chart: { type: "bar", stacked: true, toolbar: { show: false }, fontFamily: "inherit", foreColor: "#333" },
                    colors: DEVICE_STATUSES.map((s) => STATUS_COLORS[s]),
                    xaxis: { categories: analytics.timelineMonths.map((x) => x.month), labels: { rotate: -45, style: { fontSize: "11px" } } },
                    legend: { position: "top" },
                    dataLabels: { enabled: false },
                    grid: { borderColor: "#f0f2f5" },
                  }}
                  height={300}
                />
              </CardContainer>
            ) : (
              <CardContainer title="Aylık Durum Dağılımı">
                <SectionEmpty msg="Bu tarih aralığında durum logu bulunamadı." />
              </CardContainer>
            )}

            {transData?.matrix ? (
              <CardContainer title="Durum Geçiş Matrisi">
                <div className="rp-matrix-wrap">
                  <table className="rp-matrix">
                    <thead>
                      <tr>
                        <th className="rp-matrix-corner">Önceki → Yeni</th>
                        {DEVICE_STATUSES.map((s) => (
                          <th key={s} style={{ color: STATUS_COLORS[s] }}>{STATUS_TR[s]}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {DEVICE_STATUSES.map((from) => (
                        <tr key={from}>
                          <td className="rp-matrix-row-label" style={{ color: STATUS_COLORS[from] }}>{STATUS_TR[from]}</td>
                          {DEVICE_STATUSES.map((to) => {
                            const val = from === to ? null : transData.matrix[from]?.[to] || 0;
                            return (
                              <td key={to} className="rp-matrix-cell" style={{ background: val != null ? cellBg(val) : "#f8fafc", color: val != null ? cellColor(val) : "#cbd5e1" }}>
                                {val == null ? "—" : val || "·"}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContainer>
            ) : (
              <CardContainer title="Durum Geçiş Matrisi">
                <SectionEmpty msg="Geçiş verisi bulunamadı." />
              </CardContainer>
            )}
          </div>
        )}

        {/* Daily timeline area chart */}
        {!isLoading && Array.isArray(analytics?.timelineDays) && analytics.timelineDays.length > 0 && (
          <CardContainer title="Günlük Durum Aktivitesi">
            <ReactApexChart
              type="area"
              series={DEVICE_STATUSES.map((s) => ({
                name: STATUS_TR[s] || s,
                data: analytics.timelineDays.map((x) => x.counts?.[s] || 0),
              }))}
              options={{
                chart: { type: "area", stacked: true, toolbar: { show: true }, zoom: { enabled: true }, fontFamily: "inherit", foreColor: "#334155" },
                colors: DEVICE_STATUSES.map((s) => STATUS_COLORS[s]),
                xaxis: { categories: analytics.timelineDays.map((x) => x.day), labels: { rotate: -45, style: { fontSize: "10px" } } },
                dataLabels: { enabled: false },
                stroke: { width: 2, curve: "smooth" },
                fill: { type: "gradient", gradient: { opacityFrom: 0.55, opacityTo: 0.15 } },
                legend: { position: "top" },
                grid: { borderColor: "#f0f2f5" },
                tooltip: { shared: true, intersect: false },
              }}
              height={280}
            />
          </CardContainer>
        )}

        {/* Model breakdown table */}
        {!isLoading && analytics?.modelBreakdown?.length ? (
          <CardContainer title="Model Bazlı Durum Dağılımı">
            {(() => {
              const list = analytics.modelBreakdown;
              const totals = list.reduce(
                (acc, m) => ({
                  assembly: acc.assembly + (m.counts?.assembly || 0),
                  packaging: acc.packaging + (m.counts?.packaging || 0),
                  stock: acc.stock + (m.counts?.stock || 0),
                  delivered: acc.delivered + (m.counts?.delivered || 0),
                  defective: acc.defective + (m.counts?.defective || 0),
                }),
                { assembly: 0, packaging: 0, stock: 0, delivered: 0, defective: 0 },
              );
              return (
                <Table
                  columns={MODEL_TABLE_COLUMNS}
                  visibleColumns={modelVisibleColumns}
                  search={true}
                  defaultPageSize={25}
                  footer={
                    <tr>
                      {modelVisibleColumns.includes(0) && (
                        <td><strong>Toplam</strong></td>
                      )}
                      {modelVisibleColumns.includes(1) && (
                        <td><strong>{totals.assembly}</strong></td>
                      )}
                      {modelVisibleColumns.includes(2) && (
                        <td><strong>{totals.packaging}</strong></td>
                      )}
                      {modelVisibleColumns.includes(3) && (
                        <td><strong>{totals.stock}</strong></td>
                      )}
                      {modelVisibleColumns.includes(4) && (
                        <td><strong>{totals.delivered}</strong></td>
                      )}
                      {modelVisibleColumns.includes(5) && (
                        <td><strong className="rp-ref-rejected">{totals.defective}</strong></td>
                      )}
                      {modelVisibleColumns.includes(6) && (
                        <td>
                          <strong>
                            {totals.delivered + totals.defective > 0
                              ? `${Math.round((totals.delivered / (totals.delivered + totals.defective)) * 1000) / 10}%`
                              : "—"}
                          </strong>
                        </td>
                      )}
                    </tr>
                  }
                >
                  {list.map((m, i) => {
                    const yieldPct = (m.counts?.delivered || 0) + (m.counts?.defective || 0) > 0
                      ? Math.round(((m.counts?.delivered || 0) / ((m.counts?.delivered || 0) + (m.counts?.defective || 0))) * 1000) / 10
                      : null;
                    return (
                      <Tr key={i} data={m}>
                        {modelVisibleColumns.includes(0) && (
                          <Td><strong>{m.modelName || "—"}</strong></Td>
                        )}
                        {modelVisibleColumns.includes(1) && (
                          <Td>{m.counts?.assembly || 0}</Td>
                        )}
                        {modelVisibleColumns.includes(2) && (
                          <Td>{m.counts?.packaging || 0}</Td>
                        )}
                        {modelVisibleColumns.includes(3) && (
                          <Td>{m.counts?.stock || 0}</Td>
                        )}
                        {modelVisibleColumns.includes(4) && (
                          <Td><span style={{ color: STATUS_COLORS.delivered, fontWeight: 600 }}>{m.counts?.delivered || 0}</span></Td>
                        )}
                        {modelVisibleColumns.includes(5) && (
                          <Td><span className="rp-ref-rejected">{m.counts?.defective || 0}</span></Td>
                        )}
                        {modelVisibleColumns.includes(6) && (
                          <Td>
                            {yieldPct != null
                              ? <Lozenge appearance={yieldPct >= 90 ? "success-subtle" : yieldPct >= 70 ? "warning-subtle" : "danger-subtle"} isBold>{yieldPct}%</Lozenge>
                              : <span className="text-muted">—</span>
                            }
                          </Td>
                        )}
                      </Tr>
                    );
                  })}
                </Table>
              );
            })()}
          </CardContainer>
        ) : null}

        {/* Transitions detail table */}
        {!isLoading && transData?.rows?.length ? (
          <CardContainer
            title="Durum Geçiş Kayıtları"
            actions={
              <Button appearance="subtle" onClick={exportTrans} iconBefore={<FiDownload size={13} />}>
                Excel İndir
              </Button>
            }
          >
            <Table
              columns={TRANSITION_TABLE_COLUMNS}
              visibleColumns={transitionVisibleColumns}
              search={true}
              defaultPageSize={25}
            >
              {transData.rows.map((r, i) => (
                <Tr key={i} data={r}>
                  {transitionVisibleColumns.includes(0) && (
                    <Td><strong>{r.serialNumber}</strong></Td>
                  )}
                  {transitionVisibleColumns.includes(1) && (
                    <Td>{r.modelName}</Td>
                  )}
                  {transitionVisibleColumns.includes(2) && (
                    <Td>
                      {r.fromStatus && r.fromStatus !== "—"
                        ? <Lozenge appearance={STATUS_APPEARANCE[r.fromStatus] || "default-subtle"}>{STATUS_TR[r.fromStatus] || r.fromStatus}</Lozenge>
                        : <span className="text-muted">—</span>
                      }
                    </Td>
                  )}
                  {transitionVisibleColumns.includes(3) && (
                    <Td><Lozenge appearance={STATUS_APPEARANCE[r.toStatus] || "default-subtle"} isBold>{STATUS_TR[r.toStatus] || r.toStatus}</Lozenge></Td>
                  )}
                  {transitionVisibleColumns.includes(4) && (
                    <Td>{r.changedByName}</Td>
                  )}
                  {transitionVisibleColumns.includes(5) && (
                    <Td>{fmtDT(r.changedAt)}</Td>
                  )}
                  {transitionVisibleColumns.includes(6) && (
                    <Td><span className="text-muted small">{r.notes || "—"}</span></Td>
                  )}
                </Tr>
              ))}
            </Table>
          </CardContainer>
        ) : null}
      </div>
    </AppPage>
  );
};

export default withAuth(DeviceStatusReport);
